import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { PlusIcon as CreateIcon, PenIcon as EditIcon } from 'lucide-react'

import Modal from 'components/Modal'
import { CreateNewVoucher } from './create-new-voucher'
import { CreateNewVoucherMainNZ } from './create-new-voucher-main-nz'
import { CreateNewCSVoucher } from './create-new-customer-service-voucher'
import { CreateNewCSVoucherMainNZ } from './create-new-customer-service-voucher-main-nz'
import { CreateNewCampaign } from './create-new-campaign'
import { UpdateCampaign } from './update-campaign'

import { isQSRHClient } from 'setups/controller'

const projectClient = process.env.REACT_APP_PROJ

const MODAL_OPTIONS = [
  {
    name: 'Create New Marketing Campaign',
    link: '?modal=create-campaign',
    type: 'create-campaign',
    icon: CreateIcon,
  },
  {
    name: 'Create New Voucher',
    link: '?modal=create-voucher',
    type: 'create-voucher',
    icon: CreateIcon,
  },
  {
    name: 'Create Customer Service Voucher',
    link: '?modal=create-customer-service-voucher',
    type: 'create-customer-service-voucher',
    icon: CreateIcon,
  },
  {
    name: 'Update Marketing Campaign',
    link: '?modal=update-campaign',
    type: 'update-campaign',
    icon: EditIcon,
  },
] as const

const getModalOptions = () => {
  if (isQSRHClient(projectClient)) {
    return MODAL_OPTIONS
  }
  return [
    MODAL_OPTIONS[2],
    MODAL_OPTIONS[1],
    MODAL_OPTIONS[0],
    MODAL_OPTIONS[3],
  ]
}

type ModalOptionsTypesType =
  | (typeof MODAL_OPTIONS)[number]['type']
  | undefined

type ModalOptionType = (typeof MODAL_OPTIONS)[number] | null

const useCampaignVoucherListActionsModal = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams() ?? undefined
  const queryModal = searchParams.get('modal') as ModalOptionsTypesType
  const [isShowCreateModal, isShowCreateModalSet] = useState(false)
  const [isShowEditModal, isShowEditModalSet] = useState(false)
  const [modalType, modalTypeSet] = useState<ModalOptionType>(null)

  const resetModal = useCallback(() => {
    isShowCreateModalSet(false)
    isShowEditModalSet(false)
    navigate('?modal=null')
  }, [isShowCreateModalSet, isShowEditModalSet, navigate])

  const handleModal = useCallback(
    (queryModal: ModalOptionsTypesType) => {
      const modalOptions = getModalOptions()
      const filtered = modalOptions.filter(
        (item) => item.type === queryModal
      )[0]
      modalTypeSet(filtered)
    },
    [modalTypeSet]
  )

  useEffect(() => {
    if (queryModal === 'create-voucher') {
      isShowCreateModalSet(true)
      handleModal(queryModal)
    } else if (queryModal === 'create-campaign') {
      isShowCreateModalSet(true)
      handleModal(queryModal)
    } else if (queryModal === 'create-customer-service-voucher') {
      isShowCreateModalSet(true)
      handleModal(queryModal)
    } else if (queryModal === 'update-campaign') {
      isShowCreateModalSet(true)
      handleModal(queryModal)
    } else {
      resetModal()
    }
  }, [queryModal, isShowCreateModalSet, isShowEditModalSet, resetModal, handleModal])

  return {
    isShowCreateModal,
    isShowEditModal,
    resetModal,
    modalType
  }
}

export const CreateCampaigVoucherModal = () => {
  const { modalType, isShowCreateModal, resetModal } = useCampaignVoucherListActionsModal()

  return (
    <>
      <div className="flex flex-col gap-4 w-max">
        {getModalOptions().map((item) => {
          const Icon = item.icon
          return (
            <button
              key={item.name}
              className="px-3 py-1 border rounded-md border-primary text-primary"
              type="button"
            >
              <Link to={item.link} className="flex items-center gap-2">
                <Icon size={16} />
                <span className="tracking-wider font-light">{item.name}</span>
              </Link>
            </button>
          )
        })}
      </div>

      {/* MODAL: CREATE VOUCHER/CAMPAIGN */}
      {modalType && (
        <Modal
          title={modalType.name}
          open={isShowCreateModal}
          onClose={resetModal}
          noFooter
        >
          {/* Create Voucher */}
          {modalType.type === 'create-voucher' && (
            isQSRHClient(projectClient) ? (
              <CreateNewVoucher onCloseModal={resetModal} />
            ) : (
              <CreateNewVoucherMainNZ onCloseModal={resetModal} />
            )
          )}

          {/* Create Customer Service Voucher */}
          {modalType.type === 'create-customer-service-voucher' && (
            isQSRHClient(projectClient) ? (
              <CreateNewCSVoucher onCloseModal={resetModal} />
            ) : (
              <CreateNewCSVoucherMainNZ onCloseModal={resetModal} />
            )
          )}

          {/* Create Campaign */}
          {modalType.type === 'create-campaign' && (
            <CreateNewCampaign onCloseModal={resetModal} />
          )}

          {/* Update Campaign */}
          {modalType.type === 'update-campaign' && (
            <UpdateCampaign onCloseModal={resetModal} />
          )}
        </Modal>
      )}
    </>
  )
}
